import { useState, useEffect } from 'react';
import isEqual from 'lodash.isequal';
import { ToggleInput, ButtonPseudo, ButtonSize, IconEdit } from '@ui/src';
import { PaymentType } from '../../../hoc/settings/types';
import { PaymentTypeItemTypes } from './types';

import cn from './styles.module.css';

const PaymentTypeItem = ({
  paymentType,
  updatedPaymentTypes,
  setUpdatedPaymentTypes,
  setSelectedPaymentType,
  onUpdatePaymentTypes,
  setPanelIsOpen,
  isSuccess,
  editAvailable,
}: PaymentTypeItemTypes) => {
  const [defaultPaymentType, setDefaultPaymentType] = useState<{
    id: string;
    visibleToClients: boolean;
    enabled: boolean;
  }>();

  const onChangePaymentTypes = ({
    name,
    value,
  }: {
    name: string;
    value: boolean;
  }) => {
    let nextUpdatedPaymentTypes = (
      [] as { id: string; visibleToClients: boolean; enabled: boolean }[]
    ).concat(updatedPaymentTypes);

    const nextPaymentType = { ...paymentType };

    if (name === 'enabled') {
      nextPaymentType.enabled = value;

      if (!value) {
        nextPaymentType.visibleToClients = false;
      }
    } else if (name === 'visibleToClients') {
      nextPaymentType.visibleToClients = value;
    }

    if (
      nextUpdatedPaymentTypes?.find((item) => item.id === nextPaymentType.id)
    ) {
      if (
        !isEqual(defaultPaymentType, {
          id: nextPaymentType.id,
          enabled: nextPaymentType.enabled,
          visibleToClients: nextPaymentType.visibleToClients,
        })
      ) {
        nextUpdatedPaymentTypes = nextUpdatedPaymentTypes?.map((item) => {
          let result = { ...item };

          if (result.id === nextPaymentType.id) {
            result = {
              id: nextPaymentType.id,
              enabled: nextPaymentType.enabled as boolean,
              visibleToClients: nextPaymentType.visibleToClients as boolean,
            };
          }

          return result;
        });
      } else {
        nextUpdatedPaymentTypes = nextUpdatedPaymentTypes?.filter(
          (item) => item.id !== nextPaymentType.id,
        );
      }
    } else {
      nextUpdatedPaymentTypes.push({
        id: nextPaymentType.id as string,
        enabled: nextPaymentType.enabled as boolean,
        visibleToClients: nextPaymentType.visibleToClients as boolean,
      });
    }

    setUpdatedPaymentTypes(nextUpdatedPaymentTypes);
    onUpdatePaymentTypes(nextPaymentType as PaymentType);
  };

  useEffect(() => {
    if (paymentType && !defaultPaymentType) {
      setDefaultPaymentType({
        id: paymentType.id,
        enabled: paymentType.enabled,
        visibleToClients: paymentType.visibleToClients,
      });
    }
  }, [paymentType, defaultPaymentType]);

  useEffect(() => {
    if (isSuccess && paymentType) {
      setDefaultPaymentType({
        id: paymentType.id,
        enabled: paymentType.enabled,
        visibleToClients: paymentType.visibleToClients,
      });
    }
  }, [isSuccess, paymentType]);

  return (
    <div className={cn.paymentTypeItem}>
      <div className={cn.paymentTypeItemTitle}>{paymentType?.name}</div>
      <div className={cn.paymentTypeItemContent}>
        <div className={cn.toggleWrapper}>
          <ToggleInput
            name={`${paymentType?.id}-enabled`}
            checked={paymentType?.enabled}
            title="Available"
            className={`${cn.statusToggle} ${
              paymentType?.enabled ? cn.checked : ''
            }`}
            onChange={(e) => {
              onChangePaymentTypes({
                name: 'enabled',
                value: e.target.checked,
              });
            }}
            disabled={!editAvailable}
          />
          <ToggleInput
            name={`${paymentType?.id}-visibleToClients`}
            checked={paymentType?.visibleToClients}
            title="Customer-accessible"
            className={paymentType?.enabled ? cn.checked : ''}
            disabled={!paymentType?.enabled || !editAvailable}
            onChange={(e) =>
              onChangePaymentTypes({
                name: 'visibleToClients',
                value: e.target.checked,
              })
            }
          />
        </div>
        <div className={cn.currenciesWrapper}>
          <div className={cn.currenciesTitleWrapper}>
            <div className={cn.currenciesTitle}>Available currencies</div>
            {editAvailable && (
              <ButtonPseudo
                className={`button-white ${cn.editButton}`}
                size={ButtonSize.SMALL}
                icon={<IconEdit width={16} height={16} />}
                onClick={() => {
                  setSelectedPaymentType(paymentType?.id as string);
                  setPanelIsOpen(true);
                }}
                text="Edit"
              />
            )}
          </div>
          <div className={cn.currencies}>
            {!paymentType?.currencies?.edges.length && (
              <div>No available currencies yet</div>
            )}
            {paymentType?.currencies?.edges?.map((item) => (
              <div className={cn.currency} key={item.node.id}>
                {item.node.name}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentTypeItem;
